import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { AccountTypes, PacAccountTypes } from '@core/constants';
import { IProvider, IProviderSearchResult, IRelevantDataProviders, IRelevantDataUserProviders, IProviderQueryParameters } from '@core/model';
import { UsersService } from '@core/services';
import { TableFilterUtility } from '@core/utilities/table-filter.utility';
import { ArrayUtils, ODataService, SecurityService, TableColumn } from '@mynexus/mynexus-ui-lib';
import { cloneDeep, isEmpty, isEqual, pick } from 'lodash-es';
import { ConfirmationService, FilterMetadata, LazyLoadEvent, MessageService, SelectItem } from 'primeng/api';
import { MultiSelect } from 'primeng/multiselect';
import { Table } from 'primeng/table';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { distinctUntilChanged, finalize, switchMap, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-assign-providers-to-user',
    templateUrl: './assign-providers-to-user.component.html',
    styleUrls: ['./assign-providers-to-user.component.scss'],
    standalone: false
})
export class AssignProvidersToUserComponent implements OnChanges, OnInit, OnDestroy {
  @ViewChild('dtp', { static: true }) dataTableProviders!: Table;
  @ViewChild('dtu', { static: true }) dataTableUserProviders!: Table;

  @ViewChild('providerListMs') providerListMs!: MultiSelect;
  @ViewChild('providerAddressMs') providerAddressMs!: MultiSelect;
  @ViewChild('providerNpiMs') providerNpiMs!: MultiSelect;
  @ViewChild('providerTaxIdMs') providerTaxIdMs!: MultiSelect;
  @ViewChild('userProvidersMs') userProvidersMs!: MultiSelect;
  @ViewChild('userAddressMs') userAddressMs!: MultiSelect;
  @ViewChild('userNpiMs') userNpiMs!: MultiSelect;
  @ViewChild('userTaxIdMs') userTaxIdMs!: MultiSelect;
  @ViewChild('accountMs') accountMs!: MultiSelect;

  private isFirstLoad = true;
  isPacAccountType!: boolean;
  isDMEPOSAccountType !: boolean;

  lazyLoadStream = new Subject<LazyLoadEvent>();

  @Input() userId!: string;
  @Input() tenantId!: string;
  @Input() accountType!: string;
  @Output() selectedProviders = new EventEmitter<boolean>();
  @Output() userProvidersChanged = new EventEmitter();

  searchTypes = [
    {
      value: 'npi',
      label: 'NPI'
    },
    {
      value: 'company',
      label: 'Parent Company'
    }
  ];

  pacProductNameParamValue = 'PAC';
  homeHealthProductNameParamValue = 'HH';
  dmeposProductNameParamValue = 'DMEPOS';
  pacFilter = { productName: { value: ['PAC', 'PAC Commercial'], matchMode: 'in' } };
  homeHealthFilter = { productName: { value: ['PAC', 'PAC Commercial', 'DMEPOS'], matchMode: 'notIn' } };
  dmeposFilter = { productName: { value: 'DMEPOS', matchMode: 'eq' } };
  unsortedFields = ['isActive'];
  searchType: string | null = null;
  searchTypeCurrent: string | null = '';
  npi!: string;
  companyName!: string;
  columns: TableColumn[];
  companies: SelectItem[] = [];
  loadingParentCompanies = false;
  productName!: string;
  physicaianAccountType: string = AccountTypes.Physician;
  searchParameter: string | null = '';

  // providers Table
  foundProviders: IProvider[] = [];
  searchedProviders: IProvider[] = [];
  foundProvidersTotalRecords = 0;
  selectAllStateProviders = false;

  isLoadingProviders = false;
  defaultSortFieldProviders = 'providerName';
  defaultSortOrderProviders = 1;
  amountOfRowsProviders = 10;
  readonly defaultFiltersProviders: LazyLoadEvent = {
    first: 0,
    rows: this.amountOfRowsProviders,
    sortField: this.defaultSortFieldProviders,
    sortOrder: this.defaultSortOrderProviders,
    filters: {},
    globalFilter: null
  };
  currentTableDefaultFilters: LazyLoadEvent = {
    filters: {}
  };
  providerTableFilters: LazyLoadEvent = {
    filters: {}
  };
  selectAllTableFilter: LazyLoadEvent = {
    filters: {}
  };
  selectAllUserTableFilter: LazyLoadEvent = {
    filters: {}
  };
  providerRelevantData!: IRelevantDataProviders;
  providerNameList: SelectItem[] = [];
  providerAddressList: SelectItem[] = [];
  npiList: SelectItem[] = [];
  taxIdList: SelectItem[] = [];
  healthPlanList: SelectItem[] = [];
  activeStateList: SelectItem[] = [];

  providerNameFilter: string[] = [];
  provideActiveFilter!: boolean | null;
  providerAddressListFilter: string[] = [];
  provideNpiFilter: string[] = [];
  provideTaxIdFilter: string[] = [];
  providerSelectedItems: IProvider[] = [];
  providerSelectedItemsCurrentState: Set<string | undefined> = new Set();
  selectAllproviderSelectedItems: IProvider[] = [];

  // user Table
  userProviders: IProvider[] = [];
  userProvidersTotalRecords = 0;
  selectAllStateUserProviders = false;
  usercount = 0;
  isLoading = false;
  isLoadingUsers = false;
  defaultSortField = 'providerName';
  defaultSortOrder = 1;
  amountOfRows = 10;
  readonly defaultFilters: LazyLoadEvent = {
    first: 0,
    rows: this.amountOfRows,
    sortField: this.defaultSortField,
    sortOrder: this.defaultSortOrder,
    filters: {},
    globalFilter: null
  };

  userTableFilters!: LazyLoadEvent;

  providerNameListProviders: SelectItem[] = [];
  providerAddressListProviders: SelectItem[] = [];
  npiListProviders: SelectItem[] = [];
  taxIdListProviders: SelectItem[] = [];
  healthPlanListProviders: SelectItem[] = [];
  activeStateListProviders: SelectItem[] = [];

  userProviderNameFilter: string[] = [];
  userActiveFilter!: boolean | null;
  userProviderAddressListFilter: string[] = [];
  userNpiFilter: string[] = [];
  userTaxIdFilter: string[] = [];
  userTenantNameListFilter: string[] = [];
  userSelectedItems: IProvider[] = [];
  private destroy$ = new Subject<void>();

  constructor(
    private readonly userService: UsersService,
    private readonly oDataService: ODataService,
    private readonly messageService: MessageService,
    private readonly securityService: SecurityService,
    private readonly confirmationService: ConfirmationService,
    private cdr: ChangeDetectorRef
  ) {
    this.columns = [
      new TableColumn('Provider Name', 'providerName'),
      new TableColumn('Active', 'isActive'),
      new TableColumn('Provider Address', 'providerAddress'),
      new TableColumn('NPI', 'npi'),
      new TableColumn('Tax ID', 'taxId'),
      new TableColumn('Account', 'tenantName')
    ];
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { userId, tenantId, accountType } = changes;

    if (userId) {
      this.loadUserProviders(this.defaultFilters);
    }

    if (tenantId) {
      this.onChangeSearchType();
    }

    if (accountType) {
      this.manageAccounts(accountType.currentValue);
    }

    if (tenantId || accountType) {
      this.loadParentCompanies(this.tenantId);
    }
  }

  ngOnInit() {
    this.lazyLoadStream
      .pipe(
        distinctUntilChanged(isEqual),
        takeUntil(this.destroy$),
      )
      .subscribe(event => this.reloadUserProviders(event));

    this.reloadUserProviders(this.defaultFilters);
  }

  manageAccounts(accountType: string): void {
    const isAuthorizationTypeChange = PacAccountTypes.includes(this.accountType) && !PacAccountTypes.includes(accountType)
      || !PacAccountTypes.includes(this.accountType) && PacAccountTypes.includes(accountType);

    if (isAuthorizationTypeChange) {
      this.onChangeSearchType();
    }

    this.isPacAccountType = AccountTypes.PostAcuteFacility === accountType || AccountTypes.HospitalCMDischargePlanner === accountType;
    this.isDMEPOSAccountType = AccountTypes.DMEPOSProvider === accountType;
  }

  onChangeSearchType(): void {
    this.foundProviders = [];
    this.providerSelectedItems = [];
    this.npi = '';
    this.searchTypeCurrent = '';
    this.companyName = '';
    this.clearProvidersTable();
  }

  loadParentCompanies(tenantId: string): void {
    if (this.isDMEPOSAccountType) {
      this.productName = this.dmeposProductNameParamValue;
    }
    else {
      this.productName = this.isPacAccountType ? this.pacProductNameParamValue : this.homeHealthProductNameParamValue;
    }

    if (tenantId) {
      this.loadingParentCompanies = true;
      this.companies = [];
      this.userService
        .getParentCompanyNames(tenantId, this.productName)
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => {
            this.loadingParentCompanies = false;
          })
        )
        .subscribe(res => {
          if (res && res.length) {
            this.companies = res
              .filter(el => el)
              .map(el => ({
                label: el,
                value: el
              }));
          }
        });
    }
  }

  filterTableProviders(value: string[] | number[] | null, criteria: string, matchMode: string): void {
    this.dataTableProviders.filter(value, criteria, matchMode);

    if (this.providerListMs?.hide) this.providerListMs.hide();
    if (this.providerAddressMs?.hide) this.providerAddressMs.hide();
    if (this.providerNpiMs?.hide) this.providerNpiMs.hide();
    if (this.providerTaxIdMs?.hide) this.providerTaxIdMs.hide();
  }

  filterTableUserProviders(value: string[] | number[] | string | boolean | null, criteria: string, matchMode: string): void {
    this.dataTableUserProviders.filter(value, criteria, matchMode);
    
    const currentFilters = this.dataTableUserProviders.createLazyLoadMetadata();
    if (currentFilters) {
      this.reloadUserProviders(currentFilters, false);
    }

    if (this.userProvidersMs?.hide) this.userProvidersMs.hide();
    if (this.userAddressMs?.hide) this.userAddressMs.hide();
    if (this.userNpiMs?.hide) this.userNpiMs.hide();
    if (this.userTaxIdMs?.hide) this.userTaxIdMs.hide();
    if (this.accountMs?.hide) this.accountMs.hide();
  }

  loadUserProviders(filter: LazyLoadEvent): void {
    if (!this.userId) {
      return;
    }
     this.selectAllUserTableFilter = filter;
    this.userTableFilters = filter;
    this.isLoadingUsers = true;
    const queryParamsDto = filter
      ? TableFilterUtility.convertToProviderQueryParams(filter)
      : TableFilterUtility.convertToProviderQueryParams(this.defaultFilters);

    forkJoin([
      this.getProvidersByUserId(this.userId, queryParamsDto),
      this.getRelevantDataUserProviders(this.userId),
    ])
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.isLoadingUsers = false)
      )
      .subscribe(([providers, providerRelevantData]) => {
        this.usercount = providers?.count;
        this.setupData(providers, providerRelevantData);
        this.isFirstLoad = false;
        this.cdr.detectChanges();
      });
  }

  reloadUserProviders(filter: LazyLoadEvent, needToReloadRelevantData = false): void {
    if (this.isFirstLoad) {
      return;
    }
    this.selectAllUserTableFilter = filter;
    this.isLoadingUsers = true;
    const queryParamsDto = filter
      ? TableFilterUtility.convertToProviderQueryParams(filter)
      : TableFilterUtility.convertToProviderQueryParams(this.defaultFilters);

    forkJoin([
      this.userService.getProvidersByUserId(this.userId, queryParamsDto),
      needToReloadRelevantData ? this.userService.getProvidersByUserIdRelevantData(this.userId) :
        this.getRelevantDataUserProviders(this.userId),
    ])
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.isLoadingUsers = false)
      )
      .subscribe(([providers, providerRelevantData]) => {
          this.usercount = providers.count;
          this.setupData(providers, providerRelevantData);
      });
  }

  loadProviders(filters: LazyLoadEvent, searchParam?: string): void {
    if (this.tenantId) {
      const providerSelectedItems = Array.from(this.providerSelectedItemsCurrentState);

      this.isLoadingProviders = true;
      if (searchParam === 'npi') {
        this.providerTableFilters.filters = {
          ...filters.filters,
          ...{ npi: { value: this.npi, matchMode: 'eq' } },
          ...providerSelectedItems.length ? { providerAccountId: { value: providerSelectedItems, matchMode: 'notIn' } } : null
        };
      }

      if (searchParam === 'company') {
        this.providerTableFilters.filters = {
          ...filters.filters,
          ...{ parentCompany: { value: this.companyName, matchMode: 'eq' } },
          ...providerSelectedItems.length ? { providerAccountId: { value: providerSelectedItems, matchMode: 'notIn' } } : null
        };
      }

      this.providerTableFilters.filters = {
        ...this.providerTableFilters.filters,
        ...this.isPacAccountType ? this.pacFilter : (this.isDMEPOSAccountType ? this.dmeposFilter : this.homeHealthFilter)
      };

      const queryParams = this.oDataService.buildQuery(this.providerTableFilters);
      this.userService
        .getProviderAccounts(this.tenantId, queryParams)
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => this.isLoadingProviders = false)
        )
        .subscribe(res => {
          this.foundProviders = res.items;
          this.foundProvidersTotalRecords = res.count;
          this.setOptionsList(this.providerRelevantData);
        });

    }
  }

  disableSearch(): boolean {
    return !(this.searchType && (this.companyName || this.npi));
  }

  onSearch(searchParam: string | null = ''): void {
    this.searchParameter = searchParam;
    this.selectAllStateProviders = false;
    this.providerSelectedItemsCurrentState = new Set();
    this.isLoadingProviders = true;
    this.clearProvidersTable();

    this.providerTableFilters = cloneDeep(this.defaultFiltersProviders);
    this.searchTypeCurrent = searchParam;
    const filterRelevant = {
      filters: this.isPacAccountType ? this.pacFilter : this.isDMEPOSAccountType ? this.dmeposFilter : this.homeHealthFilter
    };

    if (searchParam === 'npi') {
      filterRelevant.filters = {
        ...filterRelevant.filters,
        ...{ npi: { value: this.npi, matchMode: 'eq' } }
      };
      this.providerTableFilters.filters = filterRelevant.filters;
      this.selectAllTableFilter.filters = filterRelevant.filters;
    }

    if (searchParam === 'company') {
      filterRelevant.filters = {
        ...filterRelevant.filters,
        ...{ parentCompany: { value: this.companyName, matchMode: 'eq' } }
      };
      this.providerTableFilters.filters = filterRelevant.filters;
      this.selectAllTableFilter.filters = filterRelevant.filters;
    }

    if (this.isPacAccountType) {
      this.providerTableFilters.filters = {
        ...filterRelevant.filters,
      };
    }

    this.currentTableDefaultFilters = this.providerTableFilters;

    const filterRelevantParams = this.oDataService.buildShortQuery(filterRelevant);
    const queryParams = this.oDataService.buildQuery(this.providerTableFilters);
    if (this.tenantId) {
      const providerAccounts = this.userService.getProviderAccounts(this.tenantId, queryParams);
      const relevantData = this.userService.getProviderAccountsRelevantData(
        this.tenantId,
        filterRelevantParams
      );
      forkJoin([providerAccounts, relevantData])
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => this.isLoadingProviders = false)
        )
        .subscribe(res => {
          let providers;
          [providers, this.providerRelevantData] = res;
          this.foundProviders = providers.items;
          this.searchedProviders = this.foundProviders;
          this.foundProvidersTotalRecords = providers.count;
          this.setOptionsList(this.providerRelevantData);
        });
    }

  }

  onChangeSelectAllStateUserProviders(ev: { checked: boolean }): void {

    console.log(ev);
    if (ev.checked) {

      this.isLoadingUsers = true;
      this.selectAllUserTableFilter.rows = this.usercount;

      const queryParamsDto =  this.selectAllUserTableFilter
        ? TableFilterUtility.convertToProviderQueryParams(this.selectAllUserTableFilter)
        : TableFilterUtility.convertToProviderQueryParams(this.selectAllUserTableFilter);

      forkJoin([
        this.getProvidersByUserId(this.userId, queryParamsDto),
        this.getRelevantDataUserProviders(this.userId),
      ])
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => this.isLoadingUsers = false)
        )
        .subscribe(([providers, providerRelevantData]) => {

          this.userSelectedItems = providers.items;
           if (!this.userService.hasRelevantDataByUserId(this.userId)) {
          this.userService.setRelevantDataByUserId(this.userId, providerRelevantData);
        }
        });

    } else {
      this.userSelectedItems = [];
      this.selectedProviders.emit(false);
    }
  }

  onChangeSelectAllStateProviders(ev: { checked: boolean }): void {

    if (ev.checked) {
      this.isLoadingProviders = true;
      this.selectAllStateProviders = true;

        this.selectAllTableFilter = {
          ...this.providerTableFilters,
          first: 0,
          rows: this.foundProvidersTotalRecords
        };
        const queryParams = this.oDataService.buildQuery(this.selectAllTableFilter);

        this.userService.getProviderAccounts(this.tenantId, queryParams)
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => (this.isLoadingProviders = false))
        )
        .subscribe((providers) => {
          this.providerSelectedItems = [];
          this.providerSelectedItems = providers.items;
          this.setOptionsList(this.providerRelevantData);
      });

    } else {
      this.selectAllStateProviders = false;
      this.providerSelectedItems = [];
    }
  }

  onPageChangeUserProviders(): void {

  }

  onPageChangeProviders(): void {
  }

  handleLazyLoadUserProviders(event: LazyLoadEvent): void {
    this.userTableFilters = event;
    this.lazyLoadStream.next(cloneDeep(event));
  }

  handleLazyLoadProviders(event: LazyLoadEvent): void {
    this.providerTableFilters = event;
    if (this.searchTypeCurrent) {
      this.loadProviders(event, this.searchTypeCurrent);
    }
  }

  resetSelectionUsers(): void {
    this.selectAllStateUserProviders = false;
    this.userSelectedItems = [];
  }

  resetSelectionProviders(): void {
    this.selectAllStateProviders = false;
  }

  clearLazyLoadFiltersProvidersTable(): void {
    const filters = this.dataTableProviders.filters;
    if(filters) {
      Object.keys(filters).forEach(field => {
        const filterMeta = filters[field];
        const metas: FilterMetadata[] = Array.isArray(filterMeta)
        ? filterMeta
        : [filterMeta];
        metas.forEach(meta => {
          if (meta.value !== null && meta.value !== undefined) {
          this.filterTableProviders(null, field, meta.matchMode ?? 'equals');
        }
        })
      })
    }
  }

  clearAllFilters(): void {
    this.clearLazyLoadFiltersProvidersTable();
    this.clearUserProvidersTable();
    this.clearProvidersTable();
    this.clearUserMsFilter();
    this.clearProviderMsFilter();
    this.reloadUserProviders(this.defaultFilters);
    this.reloadProviderTable(this.currentTableDefaultFilters);
  }

  reloadProviderTable(filter: LazyLoadEvent): void {
    this.currentTableDefaultFilters = filter;
    this.foundProviders = this.searchedProviders;
  }
  clearUserProvidersTable(): void {
    this.dataTableUserProviders.filters = {};
    this.dataTableUserProviders.first = 0;
    this.userProviderNameFilter = [];
    this.userActiveFilter = null;
    this.userProviderAddressListFilter = [];
    this.userNpiFilter = [];
    this.userTaxIdFilter = [];
    this.userTenantNameListFilter = [];
    this.userSelectedItems = [];
    this.selectAllStateUserProviders = false;
    this.providerNameFilter = [];
  }

  clearProvidersTable(): void {
    this.clearLazyLoadFiltersProvidersTable();
    this.dataTableProviders.first = 0;
    this.providerNameFilter = [];
    this.provideActiveFilter = null;
    this.providerAddressListFilter = [];
    this.provideNpiFilter = [];
    this.provideTaxIdFilter = [];
    this.providerSelectedItems = [];
    this.selectAllStateProviders = false;
  }

  clearUserMsFilter(): void {
    this.userProvidersMs.filterValue = '';
    this.userAddressMs.filterValue = '';
    this.userNpiMs.filterValue = '';
    this.userTaxIdMs.filterValue = '';
    this.accountMs.filterValue = '';
  }

  clearProviderMsFilter(): void {
    this.providerListMs.filterValue = '';
    this.providerAddressMs.filterValue = '';
    this.providerNpiMs.filterValue = '';
    this.providerTaxIdMs.filterValue = '';
    this.userTableFilters.filters = {};
  }

  assignProviders(): void {
    const providerAccounts: Pick<IProvider, 'providerAccountId' | 'tenantId'>[] = [];
    this.providerSelectedItems.forEach(item =>
      providerAccounts.push(pick(item, ['providerAccountId', 'tenantId']))
    );

    const body = {
      providerAccounts,
      businessType: this.accountType,
      createdBy: ''
    };

    this.userService.assignProvidersToUser(this.userId, body)
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        if (res?.length) {
          this.messageService.add({
            severity: 'success',
            summary: 'Providers added',
            key: 'success',
            detail: `Providers you selected are successfully added.`,
            life: 5000
          });
          this.reloadUserProviders(this.userTableFilters, false);
          const providerSelectedItemIds = this.providerSelectedItems.map(
            item => item.providerAccountId
          );
          this.providerSelectedItemsCurrentState = new Set([...this.providerSelectedItemsCurrentState, ...providerSelectedItemIds]);

          this.providerTableFilters.filters = Object.assign(this.providerTableFilters.filters || {}, {
            providerAccountId: { matchMode: 'notIn', value: Array.from(this.providerSelectedItemsCurrentState) }
          });

          if (this.isPacAccountType) {
            this.providerTableFilters.filters = {
              ...this.providerTableFilters.filters,
            };
          }

          this.providerTableFilters.filters = {
            ...this.providerTableFilters.filters,
            ...this.isPacAccountType ? this.pacFilter : this.homeHealthFilter
          };


          this.providerSelectedItems = [];
          this.selectAllStateProviders = false;
          this.userProvidersChanged.emit();
          this.loadProviders(this.providerTableFilters);
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            key: 'error',
            detail: `Providers you selected have already been added. Please choose other Providers.`,
            life: 5000
          });
        }
      });
  }

  removeSelectedItems(): void {
    const selectedItems: string[] = [];
    const assignedProviderAccountIds = Array.from(this.providerSelectedItemsCurrentState);

    this.userSelectedItems.forEach(item => {
      selectedItems.push(item.userTenantMapId);
      const indexOfEl = assignedProviderAccountIds.findIndex(el => el === item.providerAccountId);
      if (indexOfEl !== -1) {
        assignedProviderAccountIds.splice(indexOfEl, 1);
      }
    });

    this.providerSelectedItemsCurrentState = new Set(assignedProviderAccountIds);
    this.isLoading = true;

    this.userService.removeProvidersFromUser(this.userId, selectedItems)
      .pipe
      (takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe(() => {
        this.messageService.add({
          severity: 'success',
          summary: 'Providers removed',
          key: 'removed',
          detail: `Providers you selected are successfully removed.`,
          life: 5000
        });
        this.resetSelectionUsers();
        this.clearUserMsFilter();
        this.userProvidersChanged.emit();
        this.reloadUserProviders(this.userTableFilters, true);
        this.loadUserProviders(this.defaultFilters);
      });
  }

  onRemove(): void {
    this.confirmationService.confirm({
      header: 'Remove Providers',
      message: 'Are you sure that you want to remove providers?',
      accept: () => {
        this.removeSelectedItems();
      }
    });
  }


  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setOptionsList(relevantData: IRelevantDataProviders): void {
    if (relevantData) {
      this.providerNameListProviders = ArrayUtils.arrayToSelectItemArrayByItem(
        relevantData.providerName
      );
      this.providerAddressListProviders = ArrayUtils.arrayToSelectItemArrayByItem(
        relevantData.providerAddress
      );
      this.npiListProviders = ArrayUtils.arrayToSelectItemArrayByItem(relevantData.npi);
      this.taxIdListProviders = ArrayUtils.arrayToSelectItemArrayByItem(relevantData.taxId);
      this.activeStateListProviders = relevantData.isActive
        .map(item => (item ? { value: item, label: 'Active' } : { value: item, label: 'Inactive' }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }
  }

  rowSelectProviders(): void {
    this.selectAllStateProviders =
      this.providerSelectedItems.length === this.foundProvidersTotalRecords ||
      this.providerSelectedItems.length === this.amountOfRows;
  }

  rowUnselectProviders(): void {
    this.selectAllStateProviders = false;
  }

  rowSelectUsers(): void {
    this.selectAllStateUserProviders =
      this.userSelectedItems.length === this.userProvidersTotalRecords ||
      this.userSelectedItems.length === this.amountOfRows;
  }

  rowUnselectUsers(): void {
    this.selectAllStateUserProviders = false;
  }

  disableUserProviderFilters(): boolean {
    return !this.userProviders.length && !this.healthPlanList.length;
  }

  get disabledClearAllButton(): boolean {
    const isSortAppliedProviders = !(
      this.dataTableProviders.sortOrder !== this.defaultSortOrderProviders ||
      this.dataTableProviders.sortField !== this.defaultSortFieldProviders
    );
    const isSortAppliedUsers = !(
      this.dataTableUserProviders.sortOrder !== this.defaultSortOrder ||
      this.dataTableUserProviders.sortField !== this.defaultSortField
    );

    return isSortAppliedUsers && isSortAppliedProviders
      && this.isFilterEmpty(this.userTableFilters)
      && this.isFilterEqual(this.providerTableFilters, this.currentTableDefaultFilters);
  }

  isFilterEmpty(tableFilters: LazyLoadEvent): boolean {
    return isEmpty(tableFilters?.filters);
  }

  isFilterEqual(tableFilters: LazyLoadEvent, defaultFilters: LazyLoadEvent): boolean {
    const filters = {...tableFilters?.filters};

    if (!defaultFilters.filters?.providerAccountId && filters?.providerAccountId) {
      delete filters.providerAccountId;
    }

    return isEqual(filters, defaultFilters?.filters);
  }

  disableAssignButton(): boolean {
    return (this.securityService.hasAccess(['ExternalUser.Write'])) ?
      !this.providerSelectedItems.length :
      true;
  }

  private getRelevantDataUserProviders(userId: string): Observable<IRelevantDataUserProviders> {
    return this.userService.hasRelevantDataByUserId(userId) ?
      this.userService.getRelevantDataByUserId(userId) :
      this.userService.getProvidersByUserIdRelevantData(userId);
  }

  private getProvidersByUserId(
    userId: string, queryParams: IProviderQueryParameters
  ): Observable<IProviderSearchResult<IProvider>> {
    return this.userService.hasProvidersDataByUserId(userId) ?
      this.userService.getProvidersDataByUserId(userId) :
      this.userService.getProvidersByUserId(userId, queryParams);
  }

  private setupData(
    providers: IProviderSearchResult<IProvider>, providerRelevantData: IRelevantDataUserProviders
  ): void {
    if (!providers) {
      return;
    }
    this.userProviders = providers.items;
    this.userProviders.length ?
      this.selectedProviders.emit(true) :
      this.selectedProviders.emit(false);

    this.userProvidersTotalRecords = providers.count;

    const relevantData = providerRelevantData;
    this.providerNameList = ArrayUtils.arrayToSelectItemArrayByItem(
      relevantData.providerNameList
    );
    this.providerAddressList = ArrayUtils.arrayToSelectItemArrayByItem(
      relevantData.providerAddressList
    );
    this.npiList = ArrayUtils.arrayToSelectItemArrayByItem(relevantData.npiList);
    this.taxIdList = ArrayUtils.arrayToSelectItemArrayByItem(relevantData.taxIdList);
    this.healthPlanList = ArrayUtils.arrayToSelectItemArrayByItem(
      relevantData.healthPlanList
    );
    this.activeStateList = relevantData.statusList
      .map(item =>
        item ? { value: item, label: 'Active' } : { value: item, label: 'Inactive' }
      )
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  shouldDisplayWarningMessageForHomeHealth(): void {
    this.isLoading = true;
    if (this.productName !== this.homeHealthProductNameParamValue) {
      this.assignProviders();
      this.isLoading = false;
      return;
    }
    const selectedProviderParentCompanyList = this.providerSelectedItems.map(provider => provider.parentCompany);
    const selectedProviderNpiList = this.providerSelectedItems.map(provider => provider.npi);

    const userProviderCompanyFilter = { filters: { parentCompany: { value: selectedProviderParentCompanyList, matchMode: 'in' } } };
    const userProviderNpiFilter = { filters: { npi: { value: selectedProviderNpiList, matchMode: 'in' } } };

    const userProviderCompanyFilterDto = TableFilterUtility.convertToProviderQueryParams(userProviderCompanyFilter);
    const userProviderNpiFilterDto = TableFilterUtility.convertToProviderQueryParams(userProviderNpiFilter);
    const queryParamsDto = TableFilterUtility.convertToProviderQueryParams(this.defaultFilters);

    this.userService.getProvidersByUserId(this.userId, queryParamsDto)
      .pipe(
        switchMap(result => {
          if (result?.items?.length > 0) {
            return forkJoin([
              this.userService.getProvidersByUserId(this.userId, userProviderNpiFilterDto),
              this.userService.getProvidersByUserId(this.userId, userProviderCompanyFilterDto)
            ]);
          }
          else {
            return of({ message: 'No items found' });
          }
        }),
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false;
        })
      ).subscribe(result => {
        if (result && (result as any).message === 'No items found') {
          this.assignProviders();
        }
        else if (result) {
          const [searchResultByNpi, searchResultByparentCompany] = result as [IProviderSearchResult<IProvider>, IProviderSearchResult<IProvider>];
          if (searchResultByNpi?.items?.length > 0 && this.isNpiExistingForUser(searchResultByNpi) ||
            searchResultByparentCompany?.items?.length > 0 && this.isParentCompaniesExistingForUser(searchResultByparentCompany)) {
            this.assignProviders();
            this.loadProviders(this.providerTableFilters);
            this.reloadProviderTable(this.currentTableDefaultFilters);
          }
          else {
            this.displayWarningMessageForHHProviderAddition();
          }
        }
      });
  }

  private displayWarningMessageForHHProviderAddition(): void {
    this.confirmationService.confirm({
      key: 'parentCompanyValidationForHH',
      header: 'Adding Unrelated Provider',
      message: `
        The Parent Company for the selected Provider does not match the Parent Company of previously added Providers.<br/>
        Are you sure you want to continue?
      `,
      acceptLabel: 'Add Provider',
      rejectLabel: 'Cancel',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: () => {
        this.assignProviders();
      }
    });
  }

  private isParentCompaniesExistingForUser(source: IProviderSearchResult<IProvider>): boolean {
    const selectedProviderParentCompanyList = this.providerSelectedItems.map(provider => provider.parentCompany);
    const usersProviderParentCompanyList = source.items.map(provider => provider.parentCompany);
    if (selectedProviderParentCompanyList?.length > 0 && this.areAllItemsPresentInArray(usersProviderParentCompanyList, selectedProviderParentCompanyList)) {
      return true;
    }
    return false;
  }

  private isNpiExistingForUser(source: IProviderSearchResult<IProvider>): boolean {
    const selectedProviderNpiList = this.providerSelectedItems.map(provider => provider.npi);
    const usersProviderNpiList = source.items.map(provider => provider.npi);

    if (selectedProviderNpiList?.length > 0 && this.areAllItemsPresentInArray(usersProviderNpiList, selectedProviderNpiList)) {
      return true;
    }
    return false;
  }

  private areAllItemsPresentInArray(source: string[], items: string[]): boolean {
    const sourceSet = new Set(source.map(element => element ? element.toLowerCase() : ""));
    return items.every(item => sourceSet.has(item ? item.toLowerCase() : ""));
  }
}
